import { useIntl, defineMessages, MessageDescriptor } from 'dibs-react-intl';
import { CategorySegmentsType } from '../QuickViewCategoryParagraph/index';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import { Maybe } from 'dibs-ts-utils/exports/Maybe';

const periodGroupMessages = defineMessages({
    antique: {
        id: 'periodGroup.antique',
        defaultMessage: 'Antique',
    },
    vintage: {
        id: 'periodGroup.vintage',
        defaultMessage: 'Vintage',
    },
    used: {
        id: 'periodGroup.used',
        defaultMessage: 'Used',
    },
});

function usePeriodGroupRegExp({ translate }: { translate: boolean }): RegExp {
    const intl = useIntl();
    let periodGroupValues = Object.keys(periodGroupMessages);
    if (translate) {
        periodGroupValues = Object.values(periodGroupMessages).reduce(
            (allTranslatedMsg: string[], msg: MessageDescriptor) => {
                const translatedMsg = intl.formatMessage(msg);
                return [...allTranslatedMsg, translatedMsg];
            },
            []
        );
    }
    return new RegExp(`\\b(${periodGroupValues.join('|')})\\b`, 'i');
}

export function swapPeriodGroup({
    text,
    periodGroup,
    periodGroupRegExp,
}: {
    text: Maybe<string>;
    periodGroup: string;
    periodGroupRegExp: RegExp;
}): Maybe<string> {
    return text?.replace(periodGroupRegExp, periodGroup);
}

export function containsValidPeriodGroup(text: Maybe<string>, periodGroupRegExp: RegExp): boolean {
    return !!text && periodGroupRegExp.test(text);
}

export function usePhotoImgAltText(title: string, periodGroup: string): string {
    const untranslatedRegExp = usePeriodGroupRegExp({ translate: false });
    const translatedRegExp = usePeriodGroupRegExp({ translate: true });
    const validPeriodGroup = containsValidPeriodGroup(periodGroup, untranslatedRegExp);
    if (validPeriodGroup) {
        let swappedTitle;
        if (containsValidPeriodGroup(title, translatedRegExp)) {
            swappedTitle = swapPeriodGroup({
                text: title,
                periodGroup,
                periodGroupRegExp: translatedRegExp,
            });
        }
        return swappedTitle ? swappedTitle : `${periodGroup} ${title}`;
    } else {
        return title;
    }
}

export function usePeriodGroupQuickView({
    pageType,
    periodGroup,
    pageHeading,
    categorySegments,
}: {
    pageType?: Maybe<string>;
    periodGroup: string;
    pageHeading?: Maybe<string>;
    categorySegments: CategorySegmentsType;
}): Maybe<string> {
    const untranslatedRegExp = usePeriodGroupRegExp({ translate: false });
    const translatedRegExp = usePeriodGroupRegExp({ translate: true });
    const validPeriodGroup = containsValidPeriodGroup(periodGroup, untranslatedRegExp);
    const pageHeadingContainsPeriodGroup = containsValidPeriodGroup(pageHeading, translatedRegExp);
    if (pageType === PAGE_TYPE.BUY && validPeriodGroup && categorySegments) {
        const { period, origin, style } = categorySegments;
        const paragraphSegments = [period, origin, style];
        let newPageHeading = pageHeading;

        // https://1stdibs.atlassian.net/browse/GROWTH-6811?focusedCommentId=459423
        if (pageHeadingContainsPeriodGroup) {
            newPageHeading = swapPeriodGroup({
                text: pageHeading,
                periodGroup,
                periodGroupRegExp: translatedRegExp,
            });
        } else {
            paragraphSegments.push(periodGroup);
        }

        return [...paragraphSegments, newPageHeading].filter(Boolean).join(' ');
    } else {
        return null;
    }
}
